/**
 * Hook for subscribing to Durable Object channels
 * Connects to a specific channel and handles real-time updates
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { getBackendUrl } from '@/lib/config/api';
import { useAuthStore } from '@/lib/stores/auth';

interface ChannelMessage {
  type: string;
  [key: string]: unknown;
}

interface UseDurableObjectChannelOptions {
  channel: string; // e.g., 'stream:status:123', 'streams:list', 'notifications:456'
  enabled?: boolean;
  onMessage?: (message: ChannelMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
}

export function useDurableObjectChannel({
  channel,
  enabled = true,
  onMessage,
  onConnect,
  onDisconnect,
  onError,
}: UseDurableObjectChannelOptions) {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const isConnectingRef = useRef(false); // Guard against concurrent connection attempts
  const suppressReconnectRef = useRef(false);
  const { token } = useAuthStore();

  // Store callbacks and token in refs so they don't trigger reconnections when they change
  const onMessageRef = useRef(onMessage);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  const onErrorRef = useRef(onError);
  const tokenRef = useRef(token);
  const channelRef = useRef(channel);
  const enabledRef = useRef(enabled);

  // Update refs when values change (without triggering reconnection)
  useEffect(() => {
    onMessageRef.current = onMessage;
    onConnectRef.current = onConnect;
    onDisconnectRef.current = onDisconnect;
    onErrorRef.current = onError;
    tokenRef.current = token;
    channelRef.current = channel;
    enabledRef.current = enabled;
  }, [onMessage, onConnect, onDisconnect, onError, token, channel, enabled]);

  // Connection logic - uses refs to avoid dependency issues
  const connect = useCallback(() => {
    if (!enabledRef.current || !channelRef.current) return;

    // Guard against concurrent connection attempts
    if (isConnectingRef.current) {
      return;
    }

    // Don't connect if already connected or connecting
    const currentState = wsRef.current?.readyState;
    if (currentState === WebSocket.OPEN || currentState === WebSocket.CONNECTING) {
      return;
    }

    isConnectingRef.current = true;

    try {
      const backendUrl = getBackendUrl();
      const wsBaseUrl = backendUrl || (typeof window !== 'undefined' ? window.location.origin : '');
      const wsUrl = `${wsBaseUrl.replace(/^https?/, (match) => match === 'https' ? 'wss' : 'ws')}/api/ws?channel=${encodeURIComponent(channelRef.current)}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        isConnectingRef.current = false;
        setIsConnected(true);
        reconnectAttempts.current = 0;
        onConnectRef.current?.();

        // Send authentication if we have a token
        if (tokenRef.current) {
          ws.send(JSON.stringify({
            type: 'auth',
            token: tokenRef.current,
          }));
        }

        // Immediately send subscribe for channels that need it.
        // We do this on open (not after auth_success) so unauthenticated viewers
        // still receive broadcasts. Auth only sets userId for posting/moderation.
        const ch = channelRef.current;
        const subscribeMatch =
          ch.match(/^stream:comments:(\d+)$/) ||
          ch.match(/^stream:status:(\d+)$/) ||
          ch.match(/^stream:polls:(\d+)$/) ||
          ch.match(/^stream:qa:(\d+)$/);
        if (subscribeMatch) {
          ws.send(JSON.stringify({
            type: 'subscribe',
            streamId: parseInt(subscribeMatch[1], 10),
          }));
        }
      };

      ws.onmessage = (event) => {
        try {
          const message: ChannelMessage = JSON.parse(event.data);

          // Handle authentication / subscription responses internally
          if (
            message.type === 'auth_success' ||
            message.type === 'auth_failed' ||
            message.type === 'subscribed'
          ) {
            return;
          }

          // Call the message handler using ref to get latest callback
          onMessageRef.current?.(message);
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onclose = (event) => {
        isConnectingRef.current = false;
        setIsConnected(false);
        onDisconnectRef.current?.();

        // Attempt to reconnect if not a clean close
        if (
          !suppressReconnectRef.current &&
          enabledRef.current &&
          !event.wasClean &&
          reconnectAttempts.current < maxReconnectAttempts
        ) {
          reconnectAttempts.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        isConnectingRef.current = false;
        console.error('WebSocket error:', error);
        onErrorRef.current?.(error);
      };
    } catch (error) {
      isConnectingRef.current = false;
      console.error('Failed to create WebSocket connection:', error);
    }
  }, []); // No dependencies - uses refs for all values

  const disconnect = useCallback(() => {
    isConnectingRef.current = false;
    suppressReconnectRef.current = true;
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((message: ChannelMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Track previous channel/enabled to detect changes
  const prevChannelRef = useRef(channel);
  const prevEnabledRef = useRef(enabled);
  const connectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasMountedRef = useRef(false);

  // Only reconnect when channel or enabled changes - use refs to avoid function recreation issues
  useEffect(() => {
    // Update refs immediately
    channelRef.current = channel;
    enabledRef.current = enabled;
    tokenRef.current = token;

    const channelChanged = prevChannelRef.current !== channel;
    const enabledChanged = prevEnabledRef.current !== enabled;

    // Clear any pending connect timeout
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }

    const shouldRunConnectionFlow = !hasMountedRef.current || channelChanged || enabledChanged;
    if (shouldRunConnectionFlow) {
      // Disconnect if channel changed or if disabled
      if (channelChanged || !enabled) {
        disconnect();
      }

      // Connect if enabled and channel is set
      if (enabled && channel) {
        // Small delay to ensure disconnect completes
        connectTimeoutRef.current = setTimeout(() => {
          connectTimeoutRef.current = null;
          suppressReconnectRef.current = false;
          connect();
        }, 0);
      }
    }

    // Update previous values
    hasMountedRef.current = true;
    prevChannelRef.current = channel;
    prevEnabledRef.current = enabled;

    // Cleanup: disconnect and clear timeouts on unmount or when dependencies change
    return () => {
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      disconnect();
    };
    
    // Intentionally only depend on channel and enabled - connect/disconnect are stable
  }, [enabled, channel]);

  return {
    isConnected,
    sendMessage,
    disconnect,
    connect,
  };
}
